/**
 * Lumina SDK Service
 * Provides a centralized wrapper around the Lumina SDK for image processing
 * Based on the official demo patterns from lumina-core/lumina-js/demo
 */

import { Client, Shared } from "@luminaphoto/lumina-js";
import type { ExportSettings, ExportFormat, TonalCurvePoint, ColorGradingCircle } from "../types";

export interface LuminaServiceConfig {
  licenseKey?: string;
  enableLogging?: boolean;
  previewQuality?: number;
  previewSize?: number;
  defaultPreviewFormat?: ExportFormat;
}

export interface ProcessedImage {
  previewUrl: string;
  originalData: ArrayBuffer;
  metadata: Shared.LoadImageResult;
}

export class LuminaService {
  private editor: Client.Editor | null = null;
  private isInitialized = false;
  private config: LuminaServiceConfig;
  private activePreviewUrl: string | null = null;
  private originalImageData: ArrayBuffer | null = null;
  private originalImageUrl: string | null = null;

  constructor(config: LuminaServiceConfig = {}) {
    this.config = {
      enableLogging: false,
      previewQuality: 90,
      previewSize: 2048,
      defaultPreviewFormat: "jpeg",
      ...config,
    };
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Construct worker path using Vite's base URL (configured in vite.config.ts)
      // Local dev: / → /lumina-js/client/worker.js
      // GitHub Pages: /lumina-js-demo/ → /lumina-js-demo/lumina-js/client/worker.js
      const workerPath = `${import.meta.env.BASE_URL}lumina-js/client/worker.js`;

      this.editor = new Client.Editor({
        licenseKey: this.config.licenseKey ?? "",
        workerPath,
        autoInitialize: false,
        enableLogging: this.config.enableLogging,
        timeout: 30000,
        previewQuality: this.config.previewQuality,
        previewSize: this.config.previewSize,
      });

      // Initialize manually to handle errors properly
      await this.editor.initialize();
      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize Lumina SDK:", error);
      if (error instanceof Shared.LuminaError) {
        const message = `Failed to initialize: ${error.message}${error.details ? ` (${error.details})` : ""}`;
        throw new Error(message);
      }
      if (error instanceof Error) {
        throw new Error(`Failed to initialize image processing engine: ${error.message}`);
      }
      throw new Error("Failed to initialize image processing engine");
    }
  }

  async loadImage(file: File): Promise<ProcessedImage> {
    if (!this.editor || !this.isInitialized) {
      throw new Error("Lumina service not initialized");
    }

    try {
      // Clean up any existing preview URL
      this.cleanupPreviewUrl();

      // Convert File to ArrayBuffer
      const buffer = await file.arrayBuffer();

      // Determine file format following demo pattern
      const format = this.getImageFormatFromFile(file);

      // Load image into SDK
      const metadata = await this.editor.loadImage(buffer, format);

      const previewResult = await this.editor.getPreview();
      this.originalImageData = previewResult.imageData.slice(0);
      this.originalImageUrl = this.arrayBufferToUrl(this.originalImageData);

      const previewUrl = this.arrayBufferToUrl(previewResult.imageData);
      this.activePreviewUrl = previewUrl;

      return {
        previewUrl,
        originalData: buffer,
        metadata,
      };
    } catch (error) {
      console.error("Failed to load image:", error);
      if (error instanceof Shared.LuminaError) {
        const message = `Failed to load image: ${error.message}${error.details ? ` (${error.details})` : ""}`;
        throw new Error(message);
      }
      if (error instanceof Error) {
        throw new Error(`Failed to load image: ${error.message}`);
      }
      throw new Error("Failed to load image. Please ensure the file is a valid image format.");
    }
  }

  // PREVIEW METHODS - For real-time slider feedback (non-destructive)

  async previewBrightness(brightness: number): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.previewBrightness(brightness);
    return this.updatePreviewUrl(result.imageData);
  }

  async previewContrast(contrast: number): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.previewContrast(contrast);
    return this.updatePreviewUrl(result.imageData);
  }

  async previewExposure(exposure: number): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.previewExposure(exposure);
    return this.updatePreviewUrl(result.imageData);
  }

  async previewSaturation(saturation: number): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.previewSaturation(saturation);
    return this.updatePreviewUrl(result.imageData);
  }

  async previewTemperature(temperature: number, tint: number = 0): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.previewTemperature(temperature, tint);
    return this.updatePreviewUrl(result.imageData);
  }

  async previewHighlightsShadows(
    highlights: number,
    shadows: number,
    midtones: number
  ): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.previewHighlightsShadows(highlights, shadows, midtones);
    return this.updatePreviewUrl(result.imageData);
  }

  async previewTonalCurve(points: TonalCurvePoint[]): Promise<string> {
    const editor = this.ensureReady();
    const curveData = this.convertTonalCurve(points);
    const result = await editor.previewTonalCurve(curveData);
    return this.updatePreviewUrl(result.imageData);
  }

  async previewColorGrading(colorGrading: {
    shadows: ColorGradingCircle;
    midtones: ColorGradingCircle;
    highlights: ColorGradingCircle;
  }): Promise<string> {
    const editor = this.ensureReady();
    const gradingData = this.convertColorGrading(colorGrading);
    const result = await editor.previewColorGrading(gradingData);
    return this.updatePreviewUrl(result.imageData);
  }

  // APPLY METHODS - For committing changes (adds to history) and returning preview URLs

  async applyBrightness(brightness: number): Promise<string> {
    const editor = this.ensureReady();
    await editor.applyBrightness(brightness);
    const result = await editor.getPreview();
    return this.updatePreviewUrl(result.imageData);
  }

  async applyContrast(contrast: number): Promise<string> {
    const editor = this.ensureReady();
    await editor.applyContrast(contrast);
    const result = await editor.getPreview();
    return this.updatePreviewUrl(result.imageData);
  }

  async applyExposure(exposure: number): Promise<string> {
    const editor = this.ensureReady();
    await editor.applyExposure(exposure);
    const result = await editor.getPreview();
    return this.updatePreviewUrl(result.imageData);
  }

  async applySaturation(saturation: number): Promise<string> {
    const editor = this.ensureReady();
    await editor.applySaturation(saturation);
    const result = await editor.getPreview();
    return this.updatePreviewUrl(result.imageData);
  }

  async applyTemperature(temperature: number, tint: number = 0): Promise<string> {
    const editor = this.ensureReady();
    await editor.applyTemperature(temperature, tint);
    const result = await editor.getPreview();
    return this.updatePreviewUrl(result.imageData);
  }

  async applyHighlightsShadows(
    highlights: number,
    shadows: number,
    midtones: number
  ): Promise<string> {
    const editor = this.ensureReady();
    await editor.applyHighlightsShadows(highlights, shadows, midtones);
    const result = await editor.getPreview();
    return this.updatePreviewUrl(result.imageData);
  }

  async applyTonalCurve(points: TonalCurvePoint[]): Promise<string> {
    const editor = this.ensureReady();
    const curveData = this.convertTonalCurve(points);
    await editor.applyTonalCurve(curveData);
    const result = await editor.getPreview();
    return this.updatePreviewUrl(result.imageData);
  }

  async applyColorGrading(colorGrading: {
    shadows: ColorGradingCircle;
    midtones: ColorGradingCircle;
    highlights: ColorGradingCircle;
  }): Promise<string> {
    const editor = this.ensureReady();
    const gradingData = this.convertColorGrading(colorGrading);
    await editor.applyColorGrading(gradingData);
    const result = await editor.getPreview();
    return this.updatePreviewUrl(result.imageData);
  }

  // UTILITY METHODS

  async getCurrentPreview(): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.getPreview();
    return this.updatePreviewUrl(result.imageData);
  }

  async reset(): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.reset();
    return this.updatePreviewUrl(result.imageData);
  }

  async undo(): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.undo();
    return this.updatePreviewUrl(result.imageData);
  }

  async redo(): Promise<string> {
    const editor = this.ensureReady();
    const result = await editor.redo();
    return this.updatePreviewUrl(result.imageData);
  }

  canUndo(): boolean {
    return this.editor?.canUndo() ?? false;
  }

  canRedo(): boolean {
    return this.editor?.canRedo() ?? false;
  }

  getAllHistoryEntries() {
    return this.editor?.historyManager.getAllEntries() ?? [];
  }

  getCurrentHistoryIndex(): number {
    return this.editor?.historyManager.getCurrentIndex() ?? 0;
  }

  async exportImage(settings: ExportSettings): Promise<Blob> {
    const editor = this.ensureReady();

    try {
      const result = await editor.exportImage(
        settings.format as Shared.ExportFormat,
        settings.quality
      );

      return new Blob([result.data], {
        type: `image/${settings.format}`,
      });
    } catch (error) {
      console.error("Failed to export image:", error);
      if (error instanceof Shared.LuminaError) {
        const message = `Failed to export: ${error.message}${error.details ? ` (${error.details})` : ""}`;
        throw new Error(message);
      }
      if (error instanceof Error) {
        throw new Error(`Failed to export image: ${error.message}`);
      }
      throw new Error("Failed to export image");
    }
  }

  getOriginalImageUrl(): string | null {
    return this.originalImageUrl;
  }

  getDefaultExportFormat(): ExportFormat {
    return "jpeg";
  }

  async updateConfig(newConfig: Partial<LuminaServiceConfig>): Promise<void> {
    const oldConfig = { ...this.config };
    this.config = { ...this.config, ...newConfig };

    const licenseKeyChanged =
      newConfig.licenseKey !== undefined && newConfig.licenseKey !== oldConfig.licenseKey;

    // Check if we need to reinitialize the editor for license key or preview settings
    const needsReinit =
      this.isInitialized &&
      this.editor &&
      (licenseKeyChanged ||
        (newConfig.previewQuality !== undefined &&
          newConfig.previewQuality !== oldConfig.previewQuality) ||
        (newConfig.previewSize !== undefined && newConfig.previewSize !== oldConfig.previewSize) ||
        (newConfig.enableLogging !== undefined &&
          newConfig.enableLogging !== oldConfig.enableLogging));

    if (needsReinit && this.editor) {
      // Store current image state if any
      const hadImage = this.editor.imageLoaded;
      const originalData = this.originalImageData;

      // Dispose current editor
      await this.editor.dispose();
      this.editor = null;
      this.isInitialized = false;

      // Reinitialize with new config
      await this.initialize();

      // If we had an image loaded, we should notify that reloading might be needed
      if (hadImage && originalData) {
        console.log("Preview settings updated. You may want to reload your image to see changes.");
      }
    } else if (licenseKeyChanged && !this.isInitialized && this.config.licenseKey?.trim()) {
      // Editor was never successfully initialized (e.g. failed due to missing key on mount),
      // but a license key is now available — attempt initialization.
      await this.initialize();
    }
  }

  async dispose(): Promise<void> {
    this.cleanupPreviewUrl();

    this.originalImageData = null;
    if (this.originalImageUrl) {
      URL.revokeObjectURL(this.originalImageUrl);
      this.originalImageUrl = null;
    }

    if (this.editor) {
      try {
        await this.editor.dispose();
      } catch (error) {
        console.warn("Error disposing Lumina editor:", error);
      }
      this.editor = null;
    }
    this.isInitialized = false;
  }

  get initialized(): boolean {
    return this.isInitialized;
  }

  get imageLoaded(): boolean {
    return this.editor?.imageLoaded ?? false;
  }

  // Private helper methods

  private ensureReady(): Client.Editor {
    if (!this.editor) {
      throw new Error("Lumina service not initialized");
    }
    if (!this.editor.imageLoaded) {
      throw new Error("No image loaded");
    }
    return this.editor;
  }

  private getImageFormatFromFile(file: File): Shared.ImageFormat {
    const fileName = file.name.toLowerCase();

    // Follow demo pattern for format detection
    if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")) {
      return "jpeg";
    } else if (fileName.endsWith(".png")) {
      return "png";
    } else if (fileName.endsWith(".webp")) {
      return "webp";
    } else if (
      fileName.endsWith(".dng") ||
      fileName.endsWith(".raw") ||
      fileName.endsWith(".cr2") ||
      fileName.endsWith(".nef") ||
      fileName.endsWith(".arw")
    ) {
      return "raw";
    }

    // Default to JPEG for unknown formats
    return "jpeg";
  }

  private arrayBufferToUrl(buffer: ArrayBuffer): string {
    // Follow demo pattern - always JPEG for previews
    const blob = new Blob([buffer], { type: "image/jpeg" });
    return URL.createObjectURL(blob);
  }

  private updatePreviewUrl(buffer: ArrayBuffer): string {
    // Create new URL
    const newUrl = this.arrayBufferToUrl(buffer);

    // Clean up old URL immediately - ZoomableImage handles the transition
    const oldUrl = this.activePreviewUrl;
    this.activePreviewUrl = newUrl;

    if (oldUrl) {
      // Use a short delay to ensure the new URL is returned first
      setTimeout(() => URL.revokeObjectURL(oldUrl), 50);
    }

    return newUrl;
  }

  private cleanupPreviewUrl(): void {
    if (this.activePreviewUrl) {
      URL.revokeObjectURL(this.activePreviewUrl);
      this.activePreviewUrl = null;
    }
  }

  private convertTonalCurve(points: TonalCurvePoint[]): Shared.TonalCurvePoints {
    // Convert from editor format to SDK format following demo pattern
    if (points.length < 2) {
      throw new Error("Tonal curve must have at least 2 points");
    }

    // Sort by X coordinate to ensure proper order
    const sortedPoints = [...points].sort((a, b) => a.x - b.x);

    const startPoint = sortedPoints[0]; // Should be x=0
    const endPoint = sortedPoints[sortedPoints.length - 1]; // Should be x=1
    const middlePoints = sortedPoints.slice(1, -1);

    return {
      startY: startPoint.y,
      endY: endPoint.y,
      middlePoints: middlePoints.map((point) => ({ x: point.x, y: point.y })),
    };
  }

  private convertColorGrading(colorGrading: {
    shadows: ColorGradingCircle;
    midtones: ColorGradingCircle;
    highlights: ColorGradingCircle;
  }): Shared.ColorGradingParams {
    // Convert from editor format to SDK format following demo pattern
    return {
      shadowHue: colorGrading.shadows.hue,
      shadowSaturation: colorGrading.shadows.saturation,
      shadowBuffer: colorGrading.shadows.blend, // blend maps to buffer
      midtoneHue: colorGrading.midtones.hue,
      midtoneSaturation: colorGrading.midtones.saturation,
      midtoneBuffer: colorGrading.midtones.blend,
      highlightHue: colorGrading.highlights.hue,
      highlightSaturation: colorGrading.highlights.saturation,
      highlightBuffer: colorGrading.highlights.blend,
    };
  }
}

// Singleton instance for app-wide use following demo pattern
export const luminaService = new LuminaService({
  enableLogging: false, // Set to true for debugging
  previewQuality: 85, // Good balance of quality vs performance
  previewSize: 2048, // Suitable for most screens
  defaultPreviewFormat: "webp", // Default preview format
});

// Cleanup on page unload following demo pattern
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", async () => {
    await luminaService.dispose();
  });
}
